import {Component, OnInit, Input, OnDestroy} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';

import {ModalService} from '@myServices/modal.service';
import {AuthenticationService} from '@myServices/authentication.service';
import {OverlayService} from '@myServices/overlay.service';
import {LoginModel} from '@myModels/User/login';
import {NotificationService} from '@myServices/notification.service';
import {PATTERN_USERNAME} from '@myApp/constants/form-validation.constants';
import {ConfirmPasswordValidator} from '../../../../../validators/password-match.validator';
import {take} from 'rxjs/operators';
import {ToastsManager} from 'ng2-toastr';
import {TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs/Subscription';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit, OnDestroy {

    @Input() isMobile: Boolean;
    public currentForm: 'login' | 'register' | 'register-finish' | 'forgot-password' | 'reset-password';
    public registerForm: FormGroup;
    public loginForm: FormGroup;
    public registerFinishForm: FormGroup;
    public forgotPasswordForm: FormGroup;
    public resetPasswordForm: FormGroup;
    public submitBtnDisabled = false;
    public qrCode = null;

    private user: LoginModel;
    private currentLoginAttempt = 0;
    private maxLoginAttempts = 5;
    private singleExecutionSubscription: Subscription;

    constructor(
        private modalService: ModalService,
        private authenticationService: AuthenticationService,
        private overlayService: OverlayService,
        private notificationService: NotificationService,
        private toastr: ToastsManager,
        private translateService: TranslateService,
    ) {
    }

    ngOnInit() {
        this.loginForm = new FormGroup({
            identity: new FormControl('', Validators.required),
            password: new FormControl('', Validators.required),
        });
        this.authenticationService.qrCode.pipe(take(1)).subscribe(code => this.qrCode = code);
        this.currentForm = 'login';
        this.initRegisterForm();
        this.initRegisterFinishForm();
        this.initForgotPasswordForm();
        this.initResetPasswordForm();
    }

    public close() {
        document.body.classList.remove('game-modal-header');
        this.modalService.destroy();
    }

    public onFormSubmit() {
        this.user = this.loginForm.value;
        this.login(this.loginForm.get('identity').value, this.loginForm.get('password').value);
    }

    private login(username, password) {
        this.overlayService.onOverlayChange(true);
        this.authenticationService.login(username, password)
        .subscribe(res => {
            this.overlayService.onOverlayChange(false);
            if (!res.status) {
                if (res.messages.indexOf('INVALID_CREDENTIALS') > -1) {
                    this.overlayService.onOverlayChange(false);
                    this.notificationService.error('LOGIN_ERROR');
                    return;
                }
                const secMessageIndex = res.messages.findIndex((msg) => msg === 'SECURITY_VERIFICATION_FAILED' || msg === 'ACCOUNT_DEACTIVATED');
                if (secMessageIndex >= 0) {
                    this.translateService.get('MESSAGES.' + res.messages[secMessageIndex]).subscribe((msg) => {
                        this.toastr.error(msg);
                    });
                    res.messages.splice(secMessageIndex, 1);
                }
                const tokenInvalidIndex = res.messages.findIndex((msg) => msg === 'TOKEN_INVALID');
                if (tokenInvalidIndex >= 0 && this.currentLoginAttempt <= this.maxLoginAttempts) {
                    this.authenticationService.initializeSite().then(data => {
                        this.authenticationService.setToken(data.token);
                        this.login(username, password);
                        this.currentLoginAttempt++;
                    });
                }
            }
            if (res.status) {
                this.overlayService.onOverlayChange(false);
                this.currentLoginAttempt = 0;
                this.close();
                this.authenticationService.loginManager(res.data.user, true);
            }
            // this.showOverlay = false;
        }, err => {
            this.overlayService.onOverlayChange(false);
            // this.showOverlay = false;
            console.log(err);
        }, () => {
        });
    }

    onRegisterSubmit() {

        if (this.registerForm.invalid || !this.qrCode) {
            return;
        }
        this.submitBtnDisabled = true;
        this.authenticationService.register(this.registerUsername.value, this.registerEmail.value,
                this.registerPassword.value, this.qrCode)
        .subscribe((resp) => {
            if (!resp.status) {
                this.processRegisterFormErrors(resp.messages);
            } else {
                this.onRegisterSuccess();
            }
        }, (error) => {
            this.submitBtnDisabled = false;
            console.log(error);
            this.notificationService.error(error);
        }, () => {
            this.submitBtnDisabled = false;
        });
    }

    onRegisterFinishSubmit() {
        if (this.registerFinishForm.invalid) {
            return;
        }
        this.submitBtnDisabled = true;
        this.authenticationService.activateRegisteredUser(this.registerFinishEmail.value, this.registerFinishNonce.value)
        .subscribe((resp) => {
            if (!resp.status) {
                const messages = resp.messages;
                const secMessageIndex = messages.findIndex((msg) => msg === 'SECURITY_VERIFICATION_FAILED');
                if (secMessageIndex >= 0) {
                    messages.splice(secMessageIndex, 1);
                    this.translateService.get('MESSAGES.SECURITY_VERIFICATION_FAILED').subscribe((msg) => {
                        this.toastr.error(msg);
                    });
                }
                const statusMessageIndex = messages.findIndex((msg) => msg === 'ACCOUNT_DEACTIVATED');
                if (statusMessageIndex >= 0) {
                    messages.splice(statusMessageIndex, 1);
                    this.translateService.get('MESSAGES.ACCOUNT_DEACTIVATED').subscribe((msg) => {
                        this.toastr.error(msg);
                    });
                }
                const codeMsgIndex = messages.findIndex((msg) => msg === 'INVALID_ACTIVATION_CODE');
                if (codeMsgIndex >= 0) {
                    messages.splice(codeMsgIndex, 1);
                    this.registerFinishNonce.setErrors({
                        'invalidValue': true
                    });
                }
                if (messages.length > 0) {
                    messages.forEach((msg) => {
                        this.toastr.error(msg);
                    });
                }
                return;
            }
            this.translateService.get('MESSAGES.EMAIL_VERIFICATION_SUCCESSFUL').subscribe((msg) => {
                this.toastr.success(msg);
            });

            // auto login user after registration
            const username = this.registerUsername.value;
            const password = this.registerPassword.value;
            this.login(username, password);

        }, () => {
            this.submitBtnDisabled = false;
        }, () => {
            this.submitBtnDisabled = false;
        });
    }

    onForgotFormSubmit() {
        if (this.forgotPasswordForm.invalid) {
            return;
        }
        this.submitBtnDisabled = true;
        this.authenticationService.forgotPassword(this.forgotEmail.value)
        .subscribe((resp) => {
            if (!resp.status) {
                this.onForgotPasswordFail(resp);
                return;
            }
            this.onForgotPasswordSuccess();

        }, (error) => {
            this.submitBtnDisabled = false;
            if (401 === error.status) {
                this.translateService.get('MESSAGES.AUTHENTICATION_ERROR').subscribe((msg) => {
                    this.toastr.error(msg);
                });
            }
        }, () => {
            this.submitBtnDisabled = false;
        });
    }

    onResetPasswordFormSubmit() {
        if (this.resetPasswordForm.invalid) {
            return;
        }
        this.submitBtnDisabled = true;
        this.authenticationService.resetPassword(this.resetPasswordEmail.value,
                this.resetPasswordPassword.value, this.resetPasswordNonce.value)
        .subscribe((resp) => {
            if (!resp.status) {
                this.onResetFail(resp);
                return;
            }
            this.onResetSuccess();

        }, (error) => {
            this.submitBtnDisabled = false;
            if (401 === error.status) {
                this.translateService.get('MESSAGES.AUTHENTICATION_ERROR').subscribe((msg) => {
                    this.toastr.error(msg);
                });
            }
        }, () => {
            this.submitBtnDisabled = false;
        });
    }

    get registerUsername() {
        return this.registerForm.get('identity');
    }

    get registerEmail() {
        return this.registerForm.get('email');
    }

    get registerPassword() {
        return this.registerForm.get('password');
    }
    get registerPasswordConfirm() {
        return this.registerForm.get('passwordConfirm');
    }

    get registerFinishEmail() {
        return this.registerFinishForm.get('email');
    }

    get registerFinishNonce() {
        return this.registerFinishForm.get('nonce');
    }

    get forgotEmail() {
        return this.forgotPasswordForm.get('email');
    }

    get resetPasswordEmail() {
        return this.resetPasswordForm.get('email');
    }

    get resetPasswordPassword() {
        return this.resetPasswordForm.get('password');
    }

    get resetPasswordPasswordConfirm() {
        return this.resetPasswordForm.get('passwordConfirm');
    }

    get resetPasswordNonce() {
        return this.resetPasswordForm.get('nonce');
    }


    private initRegisterForm() {
        this.registerForm = new FormGroup({
            identity: new FormControl('', [Validators.minLength(5), Validators.required, Validators.pattern(PATTERN_USERNAME)]),
            email: new FormControl('', [Validators.required, Validators.email]),
            password: new FormControl('', Validators.required),
            passwordConfirm: new FormControl('', Validators.required),
        }, {validators: ConfirmPasswordValidator});
    }

    private initRegisterFinishForm() {
        this.registerFinishForm = new FormGroup({
            email: new FormControl({value: '', disabled: true}),
            nonce: new FormControl('', Validators.required)
        });
    }

    private initForgotPasswordForm() {
        this.forgotPasswordForm = new FormGroup({
            email: new FormControl('', [Validators.required, Validators.email])
        });
    }

    private initResetPasswordForm() {
        this.resetPasswordForm = new FormGroup({
            email: new FormControl({value: '', disabled: true}, Validators.required),
            nonce: new FormControl('', Validators.required),
            password: new FormControl('', Validators.required),
            passwordConfirm: new FormControl('', Validators.required),
        }, {validators: ConfirmPasswordValidator});
    }

    private onRegisterSuccess() {
        this.registerFinishEmail.patchValue(this.registerEmail.value);
        this.currentForm = 'register-finish';
    }

    private processRegisterFormErrors(messages: string[]) {
        const qrMsgIndex = messages.findIndex((msg) => msg === 'INVALID_QR_CODE');
        if (qrMsgIndex >= 0) {
            messages.splice(qrMsgIndex, 1);
            this.translateService.get('TRANSACTION.INVALID_QR_CODE').subscribe((msg) => {
                this.toastr.error(msg);
            });
        }
        const usernameMsgIndex = messages.findIndex((msg) => msg === 'USERNAME_BE_UNIQUE');
        if (usernameMsgIndex >= 0) {
            messages.splice(usernameMsgIndex, 1);
            this.registerUsername.setErrors({
                'notUnique': true
            });
        }
        const emailMsgIndex = messages.findIndex((msg) => msg === 'EMAIL_BE_UNIQUE');
        if (emailMsgIndex >= 0) {
            messages.splice(emailMsgIndex, 1);
            this.registerEmail.setErrors({
                'notUnique': true
            });
        }
        const secMessageIndex = messages.findIndex((msg) => msg === 'SECURITY_VERIFICATION_FAILED');
        if (secMessageIndex >= 0) {
            messages.splice(secMessageIndex, 1);
            this.translateService.get('MESSAGES.SECURITY_VERIFICATION_FAILED').subscribe((msg) => {
                this.toastr.error(msg);
            });
        }
        const statusMessageIndex = messages.findIndex((msg) => msg === 'ACCOUNT_DEACTIVATED');
        if (statusMessageIndex >= 0) {
            messages.splice(statusMessageIndex, 1);
            this.translateService.get('MESSAGES.ACCOUNT_DEACTIVATED').subscribe((msg) => {
                this.toastr.error(msg);
            });
        }
        if (messages.length > 0) {
            messages.forEach((msg) => {
                this.toastr.error(msg);
            });
        }
    }

    private onForgotPasswordSuccess() {
        this.resetPasswordEmail.patchValue(this.forgotEmail.value);
        this.currentForm = 'reset-password';
    }

    private onForgotPasswordFail(resp) {
        const messages = resp.messages;
        const codeMsgIndex = messages.findIndex((msg) => msg === 'EMAIL_NOT_EXIST');
        if (codeMsgIndex >= 0) {
            messages.splice(codeMsgIndex, 1);
            this.forgotEmail.setErrors({
                'invalidValue': true
            });
        }
        const secMessageIndex = resp.messages.findIndex((msg) => msg === 'SECURITY_VERIFICATION_FAILED');
        if (secMessageIndex >= 0) {
            resp.messages.splice(secMessageIndex, 1);
            this.translateService.get('MESSAGES.SECURITY_VERIFICATION_FAILED').subscribe((msg) => {
                this.toastr.error(msg);
            });
        }
        const errorMessageIndex = resp.messages.findIndex((msg) => msg === 'ACCOUNT_DEACTIVATED');
        if (errorMessageIndex >= 0) {
            resp.messages.splice(errorMessageIndex, 1);
            this.translateService.get('MESSAGES.ACCOUNT_DEACTIVATED').subscribe((msg) => {
                this.toastr.error(msg);
            });
        }
        if (messages.length > 0) {
            messages.forEach((msg) => {
                this.toastr.error(msg);
            });
        }
    }

    private onResetSuccess() {

        this.translateService.get('MESSAGES.PASSWORD_RESET_SUCCESSFUL').subscribe((msg) => {
            this.toastr.success(msg);
        });

        // auto login user after reset password
        const username = this.resetPasswordEmail.value;
        const password = this.resetPasswordPassword.value;
        this.login(username, password);
    }

    private onResetFail(resp) {
        const messages = resp.messages;
        const codeMsgIndex = messages.findIndex((msg) => msg === 'INVALID_RESET_CODE');
        if (codeMsgIndex >= 0) {
            messages.splice(codeMsgIndex, 1);
            this.resetPasswordNonce.setErrors({
                'invalidValue': true
            });
        }
        const secMessageIndex = resp.messages.findIndex((msg) => msg === 'SECURITY_VERIFICATION_FAILED');
        if (secMessageIndex >= 0) {
            resp.messages.splice(secMessageIndex, 1);
            this.translateService.get('MESSAGES.SECURITY_VERIFICATION_FAILED').subscribe((msg) => {
                this.toastr.error(msg);
            });
        }
        const errorMessageIndex = resp.messages.findIndex((msg) => msg === 'ACCOUNT_DEACTIVATED');
        if (errorMessageIndex >= 0) {
            resp.messages.splice(errorMessageIndex, 1);
            this.translateService.get('MESSAGES.ACCOUNT_DEACTIVATED').subscribe((msg) => {
                this.toastr.error(msg);
            });
        }
        if (messages.length > 0) {
            messages.forEach((msg) => {
                this.toastr.error(msg);
            });
        }
    }

    ngOnDestroy(): void {
        if (this.singleExecutionSubscription) {
            this.singleExecutionSubscription.unsubscribe();
        }
    }
}



// WEBPACK FOOTER //
// C:/Users/beyn/Documents/GitLab/spinmatic-casino/src/app/Modules/Authentication/components/login/login.component.ts